import '../../../../webmodule-common/other/ui/maps/google-map.js';
import '../../../../webmodule-common/other/ui/maps/google-place-autocomplete.js';
import { AddressEditor } from '../../../../webmodule-common/other/ui/address-editor.js';
import { cache } from '../../cache-impl/cache-registry.js';
import {
  ClientDetailView,
  ClientDetailViewOptions,
  clientStateDisplayString
} from '../../../clients/views/client-detail-view.js';
import { ClientState } from '../../../api/dealer-api-interface-client.js';
import { customElement } from 'lit/decorators.js';
import { DataCacheGeneric } from '../../../../webmodule-common/cache/generic-data-cache.js';
import { TabbedEntryOwner } from '../../../../webmodule-common/other/ui/data-entry-screen-base.js';
import { DevelopmentError } from '../../../../webmodule-common/other/development-error.js';
import { EventTemplate } from '../../../../webmodule-common/other/ui/events.js';
import { FieldType } from '../../../../webmodule-common/other/ui/databinding/data-tracker.js';
import {
  FormInputAssistant,
  type PickerResult
} from '../../../../webmodule-common/other/ui/templateresult/form-input-assistant.js';
import { FranchiseeClientContainerManager } from '../data/franchisee-client-container.js';
import { getApiFactory } from '../../../api/api-injector.js';
import { html } from 'lit';
import { LangFranchiseeClientDetailView } from '../../../../localization/scripts/dealer-franchisee/clients/views/franchisee-client-detail-view-lang';
import { PaymentProfileApi } from '../../../api/payment-profile-api.js';
import { PaymentProfilePicker } from '../../../franchisee/view/payment-profile-picker.js';
import { state } from '@lit/reactive-element/decorators.js';
import { TermsConditionsPicker } from '../../../franchisee/view/terms-conditions-picker';

@customElement('wm-franchiseeclientdetailview')
export class FranchiseeClientDetailView extends ClientDetailView {
  paymentProfileApi: PaymentProfileApi = getApiFactory().paymentProfile();
  paymentProfileCache: DataCacheGeneric = cache().paymentProfile;
  termsConditionsCache: DataCacheGeneric = cache().termsAndConditions;
  termsCondtionsApi = getApiFactory().termsAndConditions();

  @state()
  paymentProfileDisplay = '';

  @state()
  termsConditionsDisplay = '';

  constructor(owner: TabbedEntryOwner, options: ClientDetailViewOptions) {
    super(owner, options);

    if (!(options.clientContainerManager instanceof FranchiseeClientContainerManager))
      throw new DevelopmentError('FranchiseeClientDetailView only supports FranchiseeClientContainerManager');

    const addField = (
      fieldName: string,
      propertyType?: FieldType,
      nullable?: boolean,
      editorFieldName?: string,
      data?: () => any
    ) => {
      this.dataTracker.addObjectBinding(
        data ?? (() => this.client),
        fieldName,
        editorFieldName ?? fieldName,
        propertyType ?? FieldType.string,
        nullable ?? false
      );
    };

    addField(
      'paymentProfileId',
      FieldType.string,
      true,
      undefined,
      () => this.franchiseeClientManager.franchiseeClient
    );

    addField(
      'termsAndConditionsId',
      FieldType.string,
      true,
      undefined,
      () => this.franchiseeClientManager.franchiseeClient
    );
  }

  protected get franchiseeClientManager(): FranchiseeClientContainerManager {
    return this.clientContainerManager as FranchiseeClientContainerManager;
  }

  public async loadOrRefresh(): Promise<void> {
    await super.loadOrRefresh();
    await this.loadPickerDisplaysFromCache();
    this.requestUpdate();
  }

  public getValidationErrors(): string[] {
    const errors = super.getValidationErrors();

    if (!this.franchiseeClientManager.hasPaymentProfile())
      errors.push(LangFranchiseeClientDetailView.pleaseProvidePaymentProfile());

    return errors;
  }

  protected async loadPickerDisplaysFromCache() {
    const franchiseeClient = this.franchiseeClientManager.franchiseeClient;

    // Payment Profile
    if (this.franchiseeClientManager.hasPaymentProfile() && franchiseeClient.paymentProfileId) {
      const paymentProfile = await this.paymentProfileCache.get(franchiseeClient.paymentProfileId);
      this.paymentProfileDisplay = paymentProfile?.displayValue ?? '';
    } else {
      this.paymentProfileDisplay = '';
    }

    // Terms and Conditions
    if (this.franchiseeClientManager.hasTermsAndConditions() && franchiseeClient.termsAndConditionsId) {
      const terms = await this.termsConditionsCache.get(franchiseeClient.termsAndConditionsId);
      this.termsConditionsDisplay = terms?.displayValue ?? '';
    } else {
      this.termsConditionsDisplay = '';
    }
  }

  protected bodyTemplate(): EventTemplate {
    const forms = new FormInputAssistant(this.dataTracker, this.clientContainerManager.isReadonly());
    const readonly = this.clientContainerManager.isReadonly();

    const eventPickClientType = async (): Promise<PickerResult | undefined> => {
      await this.selectClientType();
      return undefined;
    };

    const clientTypePicker = forms.pickerRequired(
      'paymentProfileId',
      this.paymentProfileDisplay,
      eventPickClientType,
      LangFranchiseeClientDetailView.paymentProfile()
    );

    const eventPickTaC = async (): Promise<PickerResult | undefined> => {
      await this.selectTermsConditions();
      return undefined;
    };

    const taCPicker = forms.picker(
      'termsAndConditionsId',
      this.termsConditionsDisplay,
      eventPickTaC,
      LangFranchiseeClientDetailView.termsConditions()
    );

    return html`
      <form class="frm-client-details form-two-col">
        <div class="row">
          <div>
            <h2>${LangFranchiseeClientDetailView.clientDetails()}</h2>
            ${forms.textRequired('name', LangFranchiseeClientDetailView.labelName(), 100)}
            ${forms.text('businessPhoneNumber', LangFranchiseeClientDetailView.phone(), 30)} ${clientTypePicker}
            ${taCPicker}
            ${forms.radioGroup('state', ClientState, {
              title: LangFranchiseeClientDetailView.status(),
              map: clientStateDisplayString
            })}
          </div>

          <div>
            <h2>${LangFranchiseeClientDetailView.contactDetails()}</h2>
            ${this.primaryContactTemplate(forms)}
          </div>
        </div>

        <wm-addresseditor
          @wm-ae-changed=${(e: Event) => this.physicalAddressModified(e.currentTarget as AddressEditor)}
          id="physicalAddressEditor"
          .address=${this.client.physicalAddress}
          .shippingNotes=${this.client.shippingNotes ?? undefined}
          .sectionTitle=${LangFranchiseeClientDetailView.clientAddress()}
          .readonly=${readonly}
          .isDefaultShippingVisible=${true}
          .isDefaultShipping=${this.client.shipToPhysicalAddress}
        ></wm-addresseditor>
      </form>
    `;
  }

  private async selectClientType() {
    const selectedProfile = await PaymentProfilePicker(this.paymentProfileApi);
    const forms = new FormInputAssistant(this.dataTracker, false);
    if (selectedProfile) {
      forms.updatePicker('paymentProfileId', { id: selectedProfile.id, value: selectedProfile.name });
      this.paymentProfileDisplay = selectedProfile.name;
    }
    return undefined;
  }

  private async selectTermsConditions() {
    const selected = await TermsConditionsPicker(this.termsCondtionsApi);
    const forms = new FormInputAssistant(this.dataTracker, false);
    if (selected) {
      forms.updatePicker('termsAndConditionsId', { id: selected.id, value: selected.name });
      this.termsConditionsDisplay = selected.name;
    }
    return undefined;
  }
}
